/**
 * Night: the window sinks into a night sky, a crescent moon comes up, the stars come out one by one and a few
 * shooting stars streak across; then dawn glows up from the bottom and takes it all away. About 9 s.
 *
 * Everything is worked out from the scene's clock rather than stepped frame by frame, so it plays the same at
 * any frame rate. Every glow is painted once up front; a frame only fills the sky and stamps sprites.
 */

import { calm, rand, scene } from "./scene";

const TAU = Math.PI * 2;

/** When it's all gone, in ms. */
const TOTAL = 9000;

/** The beats, in ms from the start. */
interface Beats {
  /** The sky fading in from nothing. */
  skyIn: number;
  moonFrom: number;
  moonTo: number;
  /** The bright stars come out first, this far apart; the faint ones fill in after, until `starsTo`. */
  starsFrom: number;
  starGap: number;
  starsTo: number;
  /** How long one star takes to come out. */
  starIn: number;
  /** Shooting stars start between these. */
  meteorsFrom: number;
  meteorsTo: number;
  dawnFrom: number;
  dawnTo: number;
  /** Everything fades away from here until TOTAL. */
  fadeFrom: number;
}

const BEATS: Beats = {
  skyIn: 1200,
  moonFrom: 300,
  moonTo: 2200,
  starsFrom: 600,
  starGap: 190,
  starsTo: 4400,
  starIn: 600,
  meteorsFrom: 1600,
  meteorsTo: 6000,
  dawnFrom: 5400,
  dawnTo: 8000,
  fadeFrom: 7400,
};

/** Reduced motion: slower fades, and the one shooting star drifts by in the middle. */
const CALM_BEATS: Beats = {
  skyIn: 2200,
  moonFrom: 500,
  moonTo: 3000,
  starsFrom: 900,
  starGap: 300,
  starsTo: 5000,
  starIn: 1100,
  meteorsFrom: 3200,
  meteorsTo: 4200,
  dawnFrom: 5000,
  dawnTo: 8000,
  fadeFrom: 6600,
};

/** How much of the page the sky hides: enough to feel like night, with the work faintly showing through. */
const SKY_ALPHA = 0.88;
/** Top to bottom: deep navy, lifting a little toward the horizon. */
const SKY_STOPS: Array<[number, string]> = [
  [0, "5,8,28"],
  [0.55, "12,20,58"],
  [1, "30,34,82"],
];

/** One star per this many square px of window, within these bounds. */
const PX_PER_STAR = 8000;
const MIN_STARS = 70;
const MAX_STARS = 240;
/** Under reduced motion, this share of them. */
const CALM_STARS = 0.35;
/** The bright few that get a halo and a four-point sparkle. */
const BIG_STARS = 9;
const CALM_BIG_STARS = 4;
/** Star colours: white, blue-white, warm. Every core is white; these tint the fringe. */
const TINTS = ["255,255,255", "200,218,255", "255,230,196"];
/** How long dawn takes to wash out one star. Faint ones go first, the bright ones this much later. */
const STAR_WASH = 1600;
const BIG_STAR_LINGER = 1000;

/** Sprite sizes in CSS px; they're drawn scaled. */
const DOT = 24;
const GLOW = 64;
const SPARKLE = 64;
const STREAK_W = 256;
const STREAK_H = 12;
/** A shooting star's head: its dot and the glow round it. */
const HEAD = 10;
const HEAD_GLOW = 30;
const DAWN_W = 256;
const DAWN_H = 256;

/** The moon's halo reaches this many moon radii out. */
const MOON_HALO = 4.5;
/** The offset disc bitten out of the moon, in moon radii: up and right, so the lit edge faces the dawn below. */
const BITE_X = 0.3;
const BITE_Y = -0.26;
const BITE_R = 0.97;
/** Darker patches on the lit part: x, y and radius in moon radii. */
const MARIA: Array<[number, number, number]> = [
  [-0.62, 0.42, 0.16],
  [-0.35, 0.72, 0.12],
  [-0.8, 0.05, 0.11],
  [-0.1, 0.88, 0.08],
];
const HALO_STOPS: Array<[number, string]> = [
  [0, "rgba(225,232,255,0.45)"],
  [0.22, "rgba(210,222,255,0.3)"],
  [0.35, "rgba(195,210,255,0.14)"],
  [0.6, "rgba(180,198,255,0.04)"],
  [1, "rgba(170,190,255,0)"],
];
/** The moon drifts up this far as it appears, in px over ms. */
const MOON_LIFT = 30;
const MOON_RISE = 3000;

/** The milky way is haze: painted at this fraction of CSS resolution, which also softens it for free. */
const HAZE_SCALE = 0.6;
const HAZE_ALPHA = 0.9;
const HAZE_CLOUDS = 70;
const HAZE_LANES = 16;
const HAZE_DUST = 600;
/** How long dawn takes to wash the milky way out. */
const HAZE_WASH = 1800;

/** Dawn, top to bottom: nothing, then rose and coral down to gold at the horizon. */
const DAWN_STOPS: Array<[number, string]> = [
  [0, "rgba(120,80,170,0)"],
  [0.35, "rgba(175,95,165,0.2)"],
  [0.62, "rgba(240,112,132,0.48)"],
  [0.85, "rgba(255,148,104,0.78)"],
  [1, "rgba(255,188,118,0.92)"],
];
/** Brighter where the sun is about to come up. */
const SUN_STOPS: Array<[number, string]> = [
  [0, "rgba(255,226,170,0.9)"],
  [0.45, "rgba(255,186,126,0.35)"],
  [1, "rgba(255,160,110,0)"],
];
/** The glow's top edge climbs from this share of the window's height to that one. */
const DAWN_LOW = 0.25;
const DAWN_HIGH = 0.72;
const DAWN_ALPHA = 0.9;

interface Star {
  /** Position as a fraction of the window, so a resize keeps the sky. */
  fx: number;
  fy: number;
  /** Drawn size of its dot sprite, in px. */
  size: number;
  tint: number;
  /** Peak brightness, 0–1. */
  bright: number;
  born: number;
  /** When dawn starts washing it out. */
  washes: number;
  phase: number;
  speed: number;
  /** How far the twinkle dips, 0–1. */
  depth: number;
  /** How wide its sparkle is drawn; 0 for the small stars, which have none. */
  sparkle: number;
}

interface Meteor {
  start: number;
  dur: number;
  /** Where it starts, as a fraction of the window. */
  fx: number;
  fy: number;
  angle: number;
  dx: number;
  dy: number;
  /** How far it goes and how long its tail can grow, in px. */
  travel: number;
  tail: number;
  bright: number;
}

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
/** 0 before `a`, 1 after `b`, eased in between. */
const smooth = (a: number, b: number, t: number) => {
  const x = clamp((t - a) / (b - a), 0, 1);
  return x * x * (3 - 2 * x);
};
const easeOut = (x: number) => 1 - (1 - x) ** 3;
/** Roughly bell-shaped, within ±1.5. */
const spread = () => Math.random() + Math.random() + Math.random() - 1.5;
const tintOf = () => {
  const r = Math.random();
  return r < 0.6 ? 0 : r < 0.88 ? 1 : 2;
};

/** The moon's centre: in from the top right corner, by a share of the window within px bounds. */
const moonX = (w: number) => w - clamp(w * 0.13, 70, 200);
const moonY = (h: number) => clamp(h * 0.17, 70, 170);

/** A canvas `w` by `h` CSS px, painted once at `scale` device px per CSS px, to stamp each frame. */
function paint(w: number, h: number, scale: number, draw: (g: CanvasRenderingContext2D) => void): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.ceil(w * scale));
  c.height = Math.max(1, Math.ceil(h * scale));
  const g = c.getContext("2d")!;
  g.scale(scale, scale);
  draw(g);
  return c;
}

function disc(g: CanvasRenderingContext2D, x: number, y: number, r: number) {
  g.beginPath();
  g.arc(x, y, r, 0, TAU);
  g.fill();
}

/** A soft circle: a radial gradient from the centre out to `r`. */
function glow(g: CanvasRenderingContext2D, x: number, y: number, r: number, stops: Array<[number, string]>) {
  const grad = g.createRadialGradient(x, y, 0, x, y, r);
  for (const [at, color] of stops) grad.addColorStop(at, color);
  g.fillStyle = grad;
  g.fillRect(x - r, y - r, r * 2, r * 2);
}

/** A star's point of light: a white core with a tinted fringe. */
function paintDot(tint: string, scale: number) {
  const c = DOT / 2;
  return paint(DOT, DOT, scale, (g) =>
    glow(g, c, c, c, [
      [0, "rgba(255,255,255,1)"],
      [0.12, `rgba(${tint},0.9)`],
      [0.3, `rgba(${tint},0.28)`],
      [0.6, `rgba(${tint},0.06)`],
      [1, `rgba(${tint},0)`],
    ]),
  );
}

function paintGlow(tint: string, scale: number) {
  const c = GLOW / 2;
  return paint(GLOW, GLOW, scale, (g) =>
    glow(g, c, c, c, [
      [0, `rgba(${tint},0.55)`],
      [0.15, `rgba(${tint},0.28)`],
      [0.4, `rgba(${tint},0.08)`],
      [1, `rgba(${tint},0)`],
    ]),
  );
}

/** A four-point sparkle: each side bows in through the centre, pinching the points thin. */
function paintSparkle(scale: number) {
  const c = SPARKLE / 2;
  return paint(SPARKLE, SPARKLE, scale, (g) => {
    const fill = g.createRadialGradient(c, c, 0, c, c, c);
    fill.addColorStop(0, "rgba(255,255,255,1)");
    fill.addColorStop(0.3, "rgba(236,242,255,0.8)");
    fill.addColorStop(1, "rgba(200,215,255,0)");
    g.fillStyle = fill;
    g.beginPath();
    g.moveTo(c, 0);
    g.quadraticCurveTo(c, c, SPARKLE, c);
    g.quadraticCurveTo(c, c, c, SPARKLE);
    g.quadraticCurveTo(c, c, 0, c);
    g.quadraticCurveTo(c, c, c, 0);
    g.fill();
  });
}

/** A shooting star's tail, pointing right: a soft wide wedge with a thin bright one down its middle. */
function paintStreak(scale: number) {
  const mid = STREAK_H / 2;
  const wedge = (g: CanvasRenderingContext2D, half: number) => {
    g.beginPath();
    g.moveTo(0, mid);
    g.lineTo(STREAK_W, mid - half);
    g.lineTo(STREAK_W, mid + half);
    g.closePath();
    g.fill();
  };
  return paint(STREAK_W, STREAK_H, scale, (g) => {
    const wide = g.createLinearGradient(0, 0, STREAK_W, 0);
    wide.addColorStop(0, "rgba(150,180,255,0)");
    wide.addColorStop(0.75, "rgba(200,218,255,0.3)");
    wide.addColorStop(1, "rgba(235,242,255,0.6)");
    g.fillStyle = wide;
    wedge(g, mid);
    // Feather the wide wedge's edges, since there's no blur to soften them.
    const feather = g.createLinearGradient(0, 0, 0, STREAK_H);
    feather.addColorStop(0, "rgba(0,0,0,0)");
    feather.addColorStop(0.5, "rgba(0,0,0,1)");
    feather.addColorStop(1, "rgba(0,0,0,0)");
    g.globalCompositeOperation = "destination-in";
    g.fillStyle = feather;
    g.fillRect(0, 0, STREAK_W, STREAK_H);
    g.globalCompositeOperation = "source-over";
    const core = g.createLinearGradient(0, 0, STREAK_W, 0);
    core.addColorStop(0, "rgba(200,220,255,0)");
    core.addColorStop(0.6, "rgba(225,235,255,0.5)");
    core.addColorStop(1, "rgba(255,255,255,1)");
    g.fillStyle = core;
    wedge(g, 1.1);
  });
}

/** The lit crescent alone, `r` in radius: a disc with darker patches and an offset disc bitten out. */
function paintCrescent(r: number, scale: number) {
  return paint(r * 2, r * 2, scale, (g) => {
    const lit = g.createRadialGradient(r * 0.5, r * 1.4, 0, r, r, r * 1.1);
    lit.addColorStop(0, "#fffcec");
    lit.addColorStop(1, "#e6e0c4");
    g.fillStyle = lit;
    disc(g, r, r, r);
    g.globalCompositeOperation = "source-atop";
    g.fillStyle = "rgba(160,150,120,0.16)";
    for (const [x, y, s] of MARIA) disc(g, r + x * r, r + y * r, s * r);
    g.globalCompositeOperation = "destination-out";
    g.fillStyle = "#000";
    disc(g, r + r * BITE_X, r + r * BITE_Y, r * BITE_R);
  });
}

/**
 * The crescent with its halo, the disc at the centre. The halo stops at the moon's edge, so the unlit part is
 * the sky itself, lifted a touch by earthshine: it matches the sky over a light page or a dark one.
 */
function paintMoon(r: number, scale: number) {
  const half = r * MOON_HALO;
  const crescent = paintCrescent(r, scale);
  return paint(half * 2, half * 2, scale, (g) => {
    const c = half;
    // The halo leans a little toward the lit side.
    glow(g, c - r * 0.15, c + r * 0.13, half * 0.94, HALO_STOPS);
    g.globalCompositeOperation = "destination-out";
    g.fillStyle = "#000";
    // A hair inside the edge, so no seam of sky shows round the lit rim.
    disc(g, c, c, r - 0.5);
    g.globalCompositeOperation = "source-over";
    g.fillStyle = "rgba(170,186,235,0.07)";
    disc(g, c, c, r);
    g.drawImage(crescent, c - r, c - r, r * 2, r * 2);
  });
}

/** The milky way across the sky, and the countless faint stars that never twinkle. */
function paintMilkyWay(w: number, h: number, quiet: boolean) {
  return paint(w, h, HAZE_SCALE, (g) => {
    // A band from just off the top left to the bottom, well clear of the moon.
    const x0 = w * 0.04;
    const y0 = -h * 0.05;
    const dx = w * 0.68;
    const dy = h * 1.1;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const width = Math.min(w, h) * 0.12;
    const bend = rand(0, TAU);
    const knot = rand(0.4, 0.7);
    /** A point `s` of the way along the band and `off` to one side. The band wanders, or it reads as a searchlight. */
    const along = (s: number, off: number): [number, number] => {
      const o = off + Math.sin(s * 5 + bend) * width * 0.35;
      return [x0 + dx * s + nx * o, y0 + dy * s + ny * o];
    };
    /** A soft patch, drawn out along the band. */
    const patch = (s: number, off: number, r: number, stretch: number, stops: Array<[number, string]>) => {
      const [x, y] = along(s, off);
      g.save();
      g.translate(x, y);
      g.rotate(Math.atan2(dy, dx));
      g.scale(stretch, 1);
      glow(g, 0, 0, r, stops);
      g.restore();
    };
    for (let i = 0; i < HAZE_CLOUDS; i++) {
      // Most anywhere along it, of uneven brightness; the rest bunched into one bright, warmer knot.
      const inKnot = i < HAZE_CLOUDS / 5;
      const tone = Math.random() < (inKnot ? 0.5 : 0.2) ? "255,214,200" : "170,182,255";
      const a = inKnot ? 0.06 : rand(0.025, 0.065);
      const s = inKnot ? knot + spread() * 0.06 : Math.random();
      patch(s, spread() * width * (inKnot ? 0.3 : 0.55), width * (inKnot ? rand(0.3, 0.6) : rand(0.35, 1)), 1.5, [
        [0, `rgba(${tone},${a.toFixed(3)})`],
        [1, `rgba(${tone},0)`],
      ]);
    }
    // Dark lanes of dust down the middle break up the glow.
    g.globalCompositeOperation = "destination-out";
    for (let i = 0; i < HAZE_LANES; i++) {
      patch(Math.random(), spread() * width * 0.2, width * rand(0.2, 0.4), 3.5, [
        [0, "rgba(0,0,0,0.3)"],
        [1, "rgba(0,0,0,0)"],
      ]);
    }
    g.globalCompositeOperation = "source-over";
    const dust = quiet ? HAZE_DUST / 2 : HAZE_DUST;
    for (let i = 0; i < dust; i++) {
      // Two in three crowd into the band; the rest scatter anywhere.
      const [bx, by] = along(Math.random(), spread() * width * 0.6);
      const inBand = i % 3 !== 0;
      const x = inBand ? bx : Math.random() * w;
      const y = inBand ? by : Math.random() * h;
      const r = rand(0.4, 0.75);
      g.fillStyle = `rgba(220,228,255,${rand(0.12, 0.45).toFixed(2)})`;
      g.fillRect(x - r, y - r, r * 2, r * 2);
    }
  });
}

/** Dawn as a band, stretched to the window's width and however tall the glow has risen. */
function paintDawn() {
  return paint(DAWN_W, DAWN_H, 1, (g) => {
    const band = g.createLinearGradient(0, 0, 0, DAWN_H);
    for (const [at, color] of DAWN_STOPS) band.addColorStop(at, color);
    g.fillStyle = band;
    g.fillRect(0, 0, DAWN_W, DAWN_H);
    glow(g, DAWN_W / 2, DAWN_H * 1.08, DAWN_H * 0.55, SUN_STOPS);
  });
}

function makeStars(w: number, h: number, moonR: number, quiet: boolean, beats: Beats): Star[] {
  const count = Math.round(clamp((w * h) / PX_PER_STAR, MIN_STARS, MAX_STARS) * (quiet ? CALM_STARS : 1));
  const big = quiet ? CALM_BIG_STARS : BIG_STARS;
  const fillFrom = beats.starsFrom + big * beats.starGap * 0.45;
  /** How far a point is from anywhere the moon's centre passes on its way up. */
  const fromMoon = (fx: number, fy: number) => {
    const below = fy * h - moonY(h);
    return Math.hypot(fx * w - moonX(w), below < 0 ? below : Math.max(0, below - MOON_LIFT));
  };
  const stars: Star[] = [];
  const spacing = Math.min(w, h) * 0.12;
  const crowded = (fx: number, fy: number) => stars.some((s) => Math.hypot((s.fx - fx) * w, (s.fy - fy) * h) < spacing);
  for (let i = 0; i < count; i++) {
    const sparkle = i < big ? rand(22, 34) : 0;
    // Thinner toward the horizon, where dawn comes up. None behind the moon, which shows the sky through it;
    // the sparkles stay higher still, well clear of it and of each other, and in from the edges.
    const edge = sparkle ? 0.05 : 0;
    const top = sparkle ? 0.7 : 0.95;
    const clear = moonR * (sparkle ? 4 : 1.2);
    let fx = 0;
    let fy = 0;
    for (let tries = 0; tries < 30 && (tries === 0 || fromMoon(fx, fy) < clear || (sparkle && crowded(fx, fy))); tries++) {
      fx = edge + Math.random() * (1 - 2 * edge);
      fy = edge + Math.random() ** 1.3 * (top - edge);
    }
    const bright = sparkle ? 1 : 0.3 + 0.7 * Math.random() ** 2;
    // The bright ones come out one at a time; the faint ones fill in faster and faster after.
    const born = sparkle
      ? beats.starsFrom + i * beats.starGap + rand(-40, 40)
      : fillFrom + (beats.starsTo - fillFrom) * Math.sqrt((i - big + 0.5) / Math.max(1, count - big));
    stars.push({
      fx,
      fy,
      size: sparkle ? sparkle * 0.45 : rand(5, 8) + 5 * bright,
      tint: tintOf(),
      bright,
      born,
      washes: beats.dawnFrom + (sparkle ? BIG_STAR_LINGER : rand(0, 500)) + bright * 400,
      phase: rand(0, TAU),
      speed: rand(0.003, 0.008) * (quiet ? 0.5 : 1),
      depth: (sparkle ? rand(0.15, 0.3) : rand(0.2, 0.6)) * (quiet ? 0.4 : 1),
      sparkle,
    });
  }
  return stars;
}

function meteor(start: number, dur: number, travel: number, tail: number, bright: number): Meteor {
  // Down and to one side, from a start that leaves room to cross.
  const left = Math.random() < 0.6;
  const fall = rand(0.42, 0.8);
  const angle = left ? Math.PI - fall : fall;
  return {
    start,
    dur,
    fx: left ? rand(0.4, 0.95) : rand(0.05, 0.6),
    fy: rand(0.03, 0.4),
    angle,
    dx: Math.cos(angle),
    dy: Math.sin(angle),
    travel,
    tail: Math.min(tail, travel * 0.6),
    bright,
  };
}

/** Four to six, one to a slot so they don't bunch up; under reduced motion one slow, faint one. */
function makeMeteors(w: number, h: number, quiet: boolean, beats: Beats): Meteor[] {
  const diag = Math.hypot(w, h);
  if (quiet) return [meteor(rand(beats.meteorsFrom, beats.meteorsTo), 2000, diag * 0.16, 100, 0.7)];
  const n = 4 + Math.floor(Math.random() * 3);
  const slot = (beats.meteorsTo - beats.meteorsFrom) / n;
  return Array.from({ length: n }, (_, i) =>
    meteor(beats.meteorsFrom + slot * (i + rand(0.1, 0.9)), rand(550, 900), diag * rand(0.2, 0.34), rand(120, 210), 1),
  );
}

export default function play(): Promise<void> {
  const quiet = calm();
  const beats = quiet ? CALM_BEATS : BEATS;
  const w0 = innerWidth;
  const h0 = innerHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const moonR = clamp(Math.min(w0, h0) * 0.045, 22, 46);
  const moonHalf = moonR * MOON_HALO;

  const dots = TINTS.map((tint) => paintDot(tint, dpr));
  const glows = TINTS.map((tint) => paintGlow(tint, dpr));
  const sparkle = paintSparkle(dpr);
  const streak = paintStreak(dpr);
  const moon = paintMoon(moonR, dpr);
  const milkyWay = paintMilkyWay(w0, h0, quiet);
  const dawnGlow = paintDawn();
  const stars = makeStars(w0, h0, moonR, quiet, beats);
  const meteors = makeMeteors(w0, h0, quiet, beats);

  let sky: CanvasGradient | undefined;
  let skyH = 0;

  return scene(({ ctx, w, h, t }) => {
    const fade = 1 - smooth(beats.fadeFrom, TOTAL, t);

    if (!sky || h !== skyH) {
      sky = ctx.createLinearGradient(0, 0, 0, h);
      for (const [at, color] of SKY_STOPS) sky.addColorStop(at, `rgba(${color},${SKY_ALPHA})`);
      skyH = h;
    }
    ctx.globalAlpha = smooth(0, beats.skyIn, t) * fade;
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h);

    const haze = smooth(beats.starsFrom + 1000, beats.starsTo, t) * (1 - smooth(beats.dawnFrom, beats.dawnFrom + HAZE_WASH, t));
    if (haze > 0) {
      ctx.globalAlpha = haze * HAZE_ALPHA * fade;
      ctx.drawImage(milkyWay, 0, 0, w, h);
    }

    for (const s of stars) {
      const age = t - s.born;
      if (age <= 0) continue;
      const shown = easeOut(Math.min(1, age / beats.starIn)) * (1 - smooth(s.washes, s.washes + STAR_WASH, t));
      if (shown <= 0) continue;
      const flicker = 0.6 * (0.5 + 0.5 * Math.sin(s.phase + t * s.speed)) + 0.4 * (0.5 + 0.5 * Math.sin(s.phase * 1.7 + t * s.speed * 2.3));
      const lum = s.bright * shown * (1 - s.depth * flicker) * fade;
      const x = s.fx * w;
      const y = s.fy * h;
      if (s.sparkle) {
        const halo = s.sparkle * 1.5;
        ctx.globalAlpha = lum * 0.6;
        ctx.drawImage(glows[s.tint], x - halo / 2, y - halo / 2, halo, halo);
        // The points grow as it comes out, and stretch and shrink with the twinkle.
        const size = s.sparkle * (0.4 + 0.6 * shown) * (quiet ? 0.9 : 0.7 + 0.4 * (1 - flicker));
        ctx.globalAlpha = lum;
        ctx.drawImage(sparkle, x - size / 2, y - size / 2, size, size);
      }
      ctx.globalAlpha = lum;
      ctx.drawImage(dots[s.tint], x - s.size / 2, y - s.size / 2, s.size, s.size);
    }

    const moonIn = smooth(beats.moonFrom, beats.moonTo, t);
    if (moonIn > 0) {
      const lift = quiet ? 0 : MOON_LIFT * (1 - easeOut(Math.min(1, t / MOON_RISE)));
      ctx.globalAlpha = moonIn * fade;
      ctx.drawImage(moon, moonX(w) - moonHalf, moonY(h) + lift - moonHalf, moonHalf * 2, moonHalf * 2);
    }

    for (const m of meteors) {
      const p = (t - m.start) / m.dur;
      if (p <= 0 || p >= 1) continue;
      // Slowing a little as it burns up; the tail grows out behind it, then both fade.
      const along = m.travel * p * (1.2 - 0.2 * p);
      const out = smooth(0.55, 1, p);
      const tail = Math.min(along, m.tail) * (1 - 0.5 * out);
      const x = m.fx * w + m.dx * along;
      const y = m.fy * h + m.dy * along;
      ctx.globalAlpha = m.bright * Math.min(1, p / 0.1) * (1 - out) * fade;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(m.angle);
      ctx.drawImage(streak, -tail, -STREAK_H / 2, tail, STREAK_H);
      ctx.restore();
      ctx.drawImage(glows[0], x - HEAD_GLOW / 2, y - HEAD_GLOW / 2, HEAD_GLOW, HEAD_GLOW);
      ctx.drawImage(dots[0], x - HEAD / 2, y - HEAD / 2, HEAD, HEAD);
    }

    const dawn = smooth(beats.dawnFrom, beats.dawnTo, t);
    if (dawn > 0) {
      const rise = h * (DAWN_LOW + (DAWN_HIGH - DAWN_LOW) * dawn);
      ctx.globalAlpha = dawn * DAWN_ALPHA * fade;
      ctx.drawImage(dawnGlow, 0, h - rise, w, rise);
    }

    ctx.globalAlpha = 1;
    return t < TOTAL;
  });
}
